// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
/* eslint-disable spire/fenced-imports */
import { OffsetOptions, ShiftOptions } from "@floating-ui/dom";
import { UseHoverProps } from "@floating-ui/react/dist/floating-ui.react";

import {
    FloatingArrow,
    FloatingArrowProps,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    Placement,
    useFloatingParentNodeId,
    useTransitionStyles,
} from "@floating-ui/react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TooltipContext } from "@conklin/App/Components/FloatingTooltip/TooltipContext";
import { xHeaderUpdate } from "@conklin/App/Handlers/XNG/Header";
import { FocusableSelectors, useTooltip } from "@conklin/App/Hooks/useTooltip";
import Button, { ButtonIcon, ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import { addScrollbarGutter, mergeWithCss, removeScrollbarGutter } from "@conklin/XDL/Utilities";
import React, { FC, FocusEvent, useEffect, useRef, useState } from "react";
import IconClose from "@conklin/XDL/Icons/IconClose";

export type FloatingTooltipProps = {
    isOpen?: boolean;
    placement?: Placement;
    trigger?: "hover" | "click" | "manual";
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    offset?: OffsetOptions;
    shift?: ShiftOptions;
    delay?: UseHoverProps["delay"];
    showArrow?: boolean;
    overlay?: boolean;
    triggerComponent: React.ReactNode;
    enabled?: boolean;
    drawer?: boolean;
    extendedStyles?: FloatingTooltipStyles;
};

interface Props extends FloatingTooltipProps {
    children: React.ReactNode;
    referenceId?: string;
}

export interface FloatingTooltipStyles {
    tooltipContent?: InjectableCss;
    tooltipArrow?: Omit<FloatingArrowProps, "context">;
    drawerContent?: InjectableCss;
    drawerClose?: ButtonPresentationProps;
    triggerWrapper?: InjectableCss;
}

export const floatingTooltipStyles: FloatingTooltipStyles = {};

const FloatingTooltipComponent: FC<Props> = ({
    children,
    enabled = true,
    triggerComponent,
    overlay = true,
    extendedStyles,
    showArrow = true,
    drawer = false,
    referenceId,
    trigger = "hover",
    ...options
}) => {
    const location = useAppSelector(o => getLocation(o));
    const arrowRef = useRef<SVGSVGElement>(null);
    const tooltip = useTooltip({ ...options, trigger, showArrow, arrowRef });
    const { isMounted, styles: translationStyles } = useTransitionStyles(tooltip.context);

    const [styles] = useState(() => mergeWithCss(floatingTooltipStyles, extendedStyles));
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (referenceId && typeof document !== "undefined") {
            if (!document.getElementById(referenceId)) {
                return;
            }
            tooltip.refs.setPositionReference(document.getElementById(referenceId));
        }
    }, [referenceId, isMounted]);

    // Close tooltip when scrolling outside its bounds
    useEffect(() => {
        const handleScroll = (event: Event) => {
            const tooltipElement = tooltip.refs.floating?.current;
            if (tooltipElement && event.target instanceof Node && tooltipElement.contains(event.target)) {
                return;
            }

            tooltip.setOpen(false);
            options.onOpenChange?.(false);
        };

        window.addEventListener("scroll", handleScroll, true);

        return () => {
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [tooltip.refs.floating, tooltip.setOpen]);

    useEffect(() => {
        if (overlay) {
            dispatch(
                xHeaderUpdate({
                    key: isMounted ? "registerMenu" : "unregisterMenu",
                    value: `${tooltip.nodeId}_tooltip`,
                }),
            );
            isMounted ? addScrollbarGutter() : removeScrollbarGutter();
            isMounted ? ariaAppHider.hide(document.body) : ariaAppHider.show(document.body);

            if (drawer) {
                isMounted ? document.body.classList.add("drawer-open") : document.body.classList.remove("drawer-open");
            }
        }
    }, [isMounted]);

    useEffect(() => {
        tooltip.setOpen(false);
        options.onOpenChange?.(false);
    }, [location.pathname]);

    useEffect(() => {
        return () => {
            tooltip.setOpen(false);
            options.onOpenChange?.(false);
            if (overlay) {
                dispatch(
                    xHeaderUpdate({
                        key: "unregisterMenu",
                        value: `${tooltip.nodeId}_tooltip`,
                    }),
                );
                removeScrollbarGutter();
                ariaAppHider.show(document.body);

                if (drawer) {
                    document.body.classList.remove("drawer-open");
                }
            }
        };
    }, []);

    if (!enabled) {
        return <>{triggerComponent}</>;
    }

    return (
        <FloatingNode id={tooltip.nodeId}>
            <TooltipContext.Provider value={{ ...tooltip, open: isMounted }}>
                <StyledWrapper
                    ref={tooltip.refs.setReference}
                    {...tooltip.getReferenceProps()}
                    data-state={isMounted ? "open" : "closed"}
                    {...styles.triggerWrapper}
                >
                    {triggerComponent}
                </StyledWrapper>
                <FloatingPortal>
                    <StyledWrapper
                        ref={tooltip.refs.setFloating}
                        style={
                            !drawer
                                ? {
                                      ...tooltip.floatingStyles,
                                      ...translationStyles,
                                      display: isMounted ? "block" : "none",
                                  }
                                : {}
                        }
                        {...(drawer ? styles.drawerContent : styles.tooltipContent)}
                        {...{ isMounted }}
                        {...tooltip.getFloatingProps()}
                    >
                        {drawer && (
                            <Button
                                {...styles.drawerClose}
                                onClick={() => tooltip.setOpen(false)}
                                aria-label={translate("close")}
                            >
                                <ButtonIcon src={IconClose} size={14} />
                            </Button>
                        )}
                        {children}
                        {!drawer && showArrow && (
                            <FloatingArrow ref={arrowRef} context={tooltip.context} {...styles.tooltipArrow} />
                        )}
                    </StyledWrapper>
                </FloatingPortal>
            </TooltipContext.Provider>
        </FloatingNode>
    );
};

export function FloatingTooltip(props: Props) {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
        return (
            <FloatingTree>
                <FloatingTooltipComponent {...props} />
            </FloatingTree>
        );
    }
    return <FloatingTooltipComponent {...props} />;
}

export default FloatingTooltip;
